import type { Session } from '#src/shared/session';

import type { PetitionDetailsDataExperimentsQuery } from '../experiments.graphql';
import type { FullAddressCollectionFiltersResult } from '../fullAddressCollectionFilters';

type Experiment = PetitionDetailsDataExperimentsQuery['experiments']['signFormAddressRemovalExperiment'];

type Params = {
	experiment: Experiment;
	loginState: Session['loginState'] | undefined;
	geoReach: FullAddressCollectionFiltersResult['geoReach'];
};

/**
 * Treatment is deferred to the front-end (`useTreatSignFormAddressRemovalExperiment`), so `treated` is
 * always forced to `false` here — the middle-end can return `treated: true` for a returning session even
 * with `treat: false`.
 *
 * Deferral is not a preference: `v1` only removes the address ask where an IP-derived address actually
 * exists, which depends on the session's geoip data (see `useGeolocationNormalizedValues`). That isn't
 * available in the pageUserData path, so it can only be evaluated at render time.
 *
 * The `GUEST` gate is about data integrity, not render parity: for an identified or authenticated signer
 * we may already hold a user-confirmed address, and signing writes the submitted address back to the
 * profile (see `clearUserCache` in the middle-end's `signPetition`, and monorail's `profile_changed` /
 * `new_profile_attributes`), so attaching IP-derived values there risks overwriting good data with bad.
 *
 * No `signed` gate, unlike the sibling normalizers: `getPetitionViewerInfo` hard-codes `signed: false`
 * without a `currentUserId`, and a `GUEST` session has no user — so it could never exclude anyone in this
 * experiment's population.
 */
export function normalizeSignFormAddressRemovalExperiment({ experiment, loginState, geoReach }: Params): Experiment {
	if (!shouldRunExperiment({ experiment, loginState, geoReach })) {
		return forceControlOffScope(experiment);
	}

	return { ...experiment, treated: false };
}

/**
 * Resolves an out-of-scope visitor to control **and marks it as a non-assignment**.
 *
 * The `fallback` flag is load-bearing for deferred treatment: `treated: false` is also the normal
 * in-scope state, so it can't tell the treat hook "never treat this one". `fallback || override` is the
 * platform's only signal for "not a real assignment", read back there as `isRealAssignment`. A bare
 * `{ variation: 'control' }` would be byte-identical to genuine control bucketing, so an out-of-scope
 * visitor could be treated and exposed as control, inflating that arm undetectably.
 */
function forceControlOffScope(experiment: Experiment): Experiment {
	return {
		type: experiment.type,
		experimentName: experiment.experimentName,
		config: experiment.config,
		variation: 'control',
		treated: false,
		fallback: true,
		// Keep a real upstream reason (e.g. `disabled` from the kill switch); claim `not-enrolled` only when
		// the exclusion is ours.
		fallbackReason: experiment.fallback ? experiment.fallbackReason : 'not-enrolled',
		// `override` is deliberately not carried through: the resolved-experiment type treats it and
		// `fallback` as mutually exclusive, and the forced control is our scope decision, not the override's.
	};
}

/**
 * `geoReach` is reused from the full-address-collection filters, which are already fetched
 * unconditionally in the same `Promise.all`. The two experiments are mutually exclusive by that field:
 * full-address collection is `local`-only, this one is national/global.
 */
function shouldRunExperiment({ experiment, loginState, geoReach }: Params): boolean {
	return isEnabledByFC(experiment) && loginState === 'GUEST' && (geoReach === 'national' || geoReach === 'global');
}

/**
 * An `enabledIf` failure — this experiment's own FCM `enabled` being false, including per-country, which
 * is how US-only targeting is expressed — surfaces as `fallback: true`, not a distinct sentinel. It also
 * covers resolution failures (`fetch-error`, `not-found`, …), equally unsafe to treat on.
 */
function isEnabledByFC(experiment: Experiment): boolean {
	return !experiment.fallback;
}
